import { useBulkActions } from "api/actions";
import { useAppSelector } from "app/hooks";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { createHeadItems, OLTable, Row } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { ACTIONS_LIST_ID } from "state/tables/types";
import {
  AvailableBulkResources,
  AvailableBulkResourcesUnion,
  AvailableBulkTypes,
  AvailableBulkTypesUnion,
  BulkListItem,
} from "types/actions";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListingActions } from "types/listing";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { formatNumberValue } from "utilities/numbers";

export const ActionsList = () => {
  const { message } = useMessage();
  const navigate = useNavigate();
  const { listing, updateListing } = useListing(ACTIONS_LIST_ID);
  const tables = useAppSelector(selectTables);
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();
  const { useBulkActionsList } = useBulkActions();

  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[ACTIONS_LIST_ID]?.filters?.filter(
      (item: SelectedFilter) => item.type !== "dynamic-search",
    ) || [],
  );

  const setSelectedFilters = (filters: SelectedFilterV2[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      ACTIONS_LIST_ID,
    );

    setFilters(filters);
  };

  const { data, error } = useBulkActionsList({
    filters: parseFiltersForBE(getNewFiltersOnly(filters)),
    order: listing.order,
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property,
  });

  const headItems = createHeadItems([
    {
      headerText: message({ id: "actionID" }),
      property: "bulkActionId",
      isSortable: true,
    },
    {
      headerText: message({ id: "userID" }),
      property: "userId",
      isSortable: false,
    },
    {
      headerText: message({ id: "email" }),
      property: "email",
      isSortable: false,
    },
    {
      headerText: message({ id: "comment" }),
      property: "comment",
      isSortable: false,
    },
    {
      headerText: message({ id: "resource" }),
      property: "resource",
      isSortable: false,
    },
    {
      headerText: message({ id: "actionType" }),
      property: "type",
      isSortable: true,
    },
    {
      headerText: message({ id: "numberOfRecords" }),
      property: "processedRowCount",
      isSortable: false,
    },
    {
      headerText: message({ id: "startDate" }),
      property: "requestedAt",
      isSortable: true,
    },
  ]);

  const bulkTypeMessage = (type: AvailableBulkTypesUnion) => {
    switch (type) {
      case AvailableBulkTypes.CANCEL_TRANSFERS:
        return message({ id: "cancelTransfers" });
      case AvailableBulkTypes.EXPIRE_TRANSFERS:
        return message({ id: "expireTransfers" });
      case AvailableBulkTypes.ACTIVATE_TRANSFERS:
        return message({ id: "activateTransfers" });
      default:
        return "";
    }
  };

  const bulkResourceMessage = (resource: AvailableBulkResourcesUnion) => {
    switch (resource) {
      case AvailableBulkResources.TRANSFER: {
        return message({ id: "unitTransfers" });
      }
      default: {
        return "";
      }
    }
  };

  const createItems = (items: BulkListItem[]): Row[] => {
    return items.map((item) => ({
      cells: [
        {
          content: item.bulkActionId,
          property: "bulkActionId",
          dataHjSuppress: true,
        },
        {
          content: item.userId,
          property: "userId",
          dataHjSuppress: true,
        },
        {
          content: item.email,
          property: "email",
          dataHjSuppress: true,
        },
        {
          content: item.comment,
          property: "comment",
          dataHjSuppress: true,
        },
        {
          content: bulkResourceMessage(item.resource),
          property: "resource",
          dataHjSuppress: true,
        },
        {
          content: bulkTypeMessage(item.type),
          property: "type",
          dataHjSuppress: true,
        },
        {
          align: "right",
          content: formatNumberValue(item.processedRowCount),
          property: "processedRowCount",
          dataHjSuppress: true,
        },
        {
          content: OLDate(item.requestedAt),
          property: "requestedAt",
          dataHjSuppress: true,
        },
      ],
      id: item.bulkActionId,
    }));
  };

  const items = data?.items ? createItems(data.items) : [];

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && data) {
      setPersistentFilters(filters, ACTIONS_LIST_ID);
    }
    setPersistentFilters(filters, ACTIONS_LIST_ID);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, error, data]);

  return (
    <>
      <Subheader>
        <FiltersV2
          data-test-id="actions-list"
          donutLabel={message({ id: "results" })}
          savedFilters={getNewFiltersOnly(filters)}
          properties={[
            {
              query: "bulkActionID",
              label: message({ id: "actionID" }),
              type: "text",
            },
            {
              query: "requestedAt",
              label: message({ id: "startDate" }),
              type: "datetime",
            },
            {
              query: "type",
              label: message({ id: "type" }),
              type: "bulk-actions",
            },
          ]}
          setFilters={setSelectedFilters}
          total={data?.total}
        />
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={ACTIONS_LIST_ID}
        data-test-id="actions"
        actions={{
          body: [
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                navigate(links.actionDetails(id as string));
              },
              label: message({ id: "seeActionsRecords" }),
            },
          ],
        }}
        columns={data && headItems}
        onRowClick={(id: string) => {
          navigate(links.actionDetails(id));
        }}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(data?.total.filtered, data?.total.all),
          onChangePage: (page) => {
            updateListing({ type: ListingActions.CHANGE_PAGE, payload: page });
          },
          onChangeRowsPerPage: (rows) => {
            updateListing({
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
              payload: rows,
            });
          },
          page: listing.page,
          rowsPerPage: listing.rowsPerPage,
        }}
        rows={data && items}
        sort={{
          order: listing.order,
          property: listing.property,
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
      />
    </>
  );
};
